import * as d3 from 'd3'
import * as dc from 'dc'
import { MATURITY_LABELS } from '@/utils/flowSightBuckets'
import { BUY_COLOR, SELL_COLOR, HEATMAP_NEUTRAL, HEATMAP_NODATA } from '@/utils/flowSightColors'
import { conversionRateOf, convertedVolumeOf, observationsOf, emptyConversionValue, combineConversionValues } from '../calculations/conversionRate.js'
import { formatCompactAmount } from '@/utils/utils'
import { getChartTooltip, escapeHtml } from '../runtime/chartTooltip.js'

export function createConversionHeatmapChart ({ el, chartGroup, width, height, dims, grp, rowBuckets, t, getSelectedTenorIdxs, drilldown }) {
  // Both axes read the shared ladders in flowSightBuckets.js - the same bands
  // the Overview client charts show. NOT the 8 canonical tenor buckets that
  // chart1/bubble/split on this tab are keyed by; see the note in that module.
  const COLS = MATURITY_LABELS // X: ['1-30D','31-90D','91-180D','181-360D','>=361D']
  const ROWS = rowBuckets      // Y: ['0-100k','100k-1M','1M-5M','5M-15M','>15M']

  const colIdx = (tenor) => Math.max(0, COLS.indexOf(tenor))
  const rowIdx = (vrng) => Math.max(0, ROWS.indexOf(vrng))

  const keyAcc = (d) => d.key[0] // tenor (col)
  const rowAcc = (d) => d.key[1] // volume (row)
  const cellCounts = (d) => observationsOf(d.value?.BUY) + observationsOf(d.value?.SELL)

  // The cell's conversion rate, BUY and SELL merged. Only the LABEL uses this -
  // the colour is decided by deal volume below.
  const cellRate = (cellVal) => conversionRateOf(combineConversionValues(cellVal?.BUY, cellVal?.SELL))

  // Colour = CONVERTED VOLUME in the bucket, on a sequential heat scale: the
  // question the map answers is "where does the money actually close". The
  // conversion RATE is printed inside the cell instead, so a hot square that
  // converts badly is visible as such.
  //
  // Replaces the old BUY/SELL dominance + threshold pair. One flat blue and one
  // flat amber could only say WHICH side won, never how much was at stake, and
  // the side breakdown moved to the tooltip.
  const cellVolume = (cellVal) =>
    convertedVolumeOf(cellVal?.BUY) + convertedVolumeOf(cellVal?.SELL)

  // Blues in its natural direction: the more volume closed in the bucket, the
  // DEEPER the blue. Light end for the near-empty cells.
  //
  // Note this runs against the dark card behind it - the heaviest cells are the
  // ones that recede. Deliberate call, the convention "darker = more" won over
  // the contrast argument. Empty cells stay flat grey and print "–" instead of
  // a percentage, which is what separates them from a dark, very full cell.
  const heatColor = (ratio) => d3.interpolateBlues(ratio)

  // The hot end of the ramp is nearly white and HEATMAP_NEUTRAL is light grey,
  // so a hardcoded white label would vanish on both. Pick the ink from the
  // background's perceived luminance instead.
  const labelInk = (background) => {
    const { r, g, b } = d3.rgb(background)
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6 ? '#0e0f12' : '#ffffff'
  }

  // Preenche TODAS as combinações (COLS x ROWS), para exibir "-" onde não houver dado
  const ensureAllBins = (baseGroup, colsDomain, rowsDomain) => ({
    all: () => {
      const map = new Map(baseGroup.all().map((d) => [JSON.stringify(d.key), d.value]))
      const out = []
      for (const c of colsDomain) {
        for (const r of rowsDomain) {
          const k = [c, r]
          const v = map.get(JSON.stringify(k)) || { BUY: emptyConversionValue(), SELL: emptyConversionValue() }
          out.push({ key: k, value: v })
        }
      }
      return out
    },
  })

  // Ordenação fixa pelos domínios (não deixa o X/Y "embaralhar")
  const orderedGroup = (baseGroup, colsDomain, rowsDomain) => {
    const colMap = new Map(colsDomain.map((t, i) => [t, i]))
    const rowMap = new Map(rowsDomain.map((v, i) => [v, i]))
    return {
      all: () => baseGroup.all().slice().sort((a, b) => {
        const [ta, va] = a.key || []
        const [tb, vb] = b.key || []
        const ia = colMap.has(ta) ? colMap.get(ta) : 9_999
        const ib = colMap.has(tb) ? colMap.get(tb) : 9_999
        if (ia !== ib) return ia - ib
        const ja = rowMap.has(va) ? rowMap.get(va) : 9_999
        const jb = rowMap.has(vb) ? rowMap.get(vb) : 9_999
        return ja - jb
      }),
    }
  }

  const filled = ensureAllBins(grp.grpHeat, COLS, ROWS)
  const ordered = orderedGroup(filled, COLS, ROWS)

  // Top of the ramp = the busiest cell CURRENTLY on screen, so the map keeps
  // its full contrast after a filter narrows the data instead of collapsing
  // into one dim corner. Read fresh on every draw; caching it would leave the
  // scale describing a dataset that is no longer there.
  const maxCellVolume = () => d3.max(ordered.all(), (d) => cellVolume(d.value)) || 0

  // Shared by the cell fill and by the label ink below, so the two can never
  // disagree about what colour a cell actually is.
  const cellColor = (d) => {
    const selected = new Set(getSelectedTenorIdxs())
    if (selected.size > 0 && !selected.has(COLS.indexOf(keyAcc(d)))) return HEATMAP_NEUTRAL
    if (cellCounts(d) === 0) return HEATMAP_NODATA // "-"
    const max = maxCellVolume()
    return heatColor(max > 0 ? cellVolume(d.value) / max : 0)
  }

  const chart = dc.heatMap(el, chartGroup)
  chart
    .width(width)
    // +40 to land on the same total as the conversion chart beside it, which
    // does `height + 40` from the same base. The per-chart offsets on this tab
    // are added inside each builder, so two cards only line up if their offsets
    // agree - this one used to add nothing and came out 40px short.
    .height(height + 40)
    .margins({ top: 10, right: 16, bottom: 36, left: 80 })
    .dimension(dims.dimTenorVol)
    .group(ordered)
    .controlsUseVisibility(true)
    .cols(d3.range(COLS.length))
    .rows(d3.range(ROWS.length))
    .keyAccessor((d) => colIdx(keyAcc(d)))
    .valueAccessor((d) => rowIdx(rowAcc(d)))
    .colsLabel((i) => COLS[i] ?? '')
    .rowsLabel((i) => ROWS[i] ?? '')
    .calculateColorDomain(false)
    // colorCalculator, not colors()+colorAccessor: a sequential scale cannot
    // also express "no data" or "dimmed by a drilldown", and those two have to
    // win over the ramp.
    .colorCalculator((d) => cellColor(d))
    // No .renderLabel()/.label() here: dc's HeatMap draws text for the axes and
    // a <title> per box, never a label inside the cell. The in-cell text is
    // hand-drawn in applyDecor below.
    // Same immediate HTML tooltip the bubble chart uses, instead of dc's SVG
    // <title>: a 1s dwell in OS chrome, on a chart whose whole point is
    // comparing cells, is not a tooltip anyone reads. Wired in applyDecor.
    .renderTitle(false)

  const tooltip = getChartTooltip()

  // Mirrors the bubble's layout: bold header, then one muted-label row per
  // quantity. Leads with the two the cell encodes - the volume its colour
  // shows and the rate it prints - and closes with the per-side split that the
  // old blue/amber dominance used to carry.
  const tooltipHtml = (d) => {
    const buyObs = observationsOf(d.value?.BUY)
    const sellObs = observationsOf(d.value?.SELL)
    const header = `<div style="font-weight:700;margin-bottom:4px">${escapeHtml(`${keyAcc(d)} • ${rowAcc(d)}`)}</div>`

    if (buyObs + sellObs === 0) {
      return header + `<div style="color:var(--muted,#9aa3af)">${escapeHtml(t('flowSightConversions.noObservations'))}</div>`
    }

    const row = (label, value) =>
      `<div><span style="color:var(--muted,#9aa3af)">${escapeHtml(label)}:</span> ${escapeHtml(value)}</div>`
    const buyVol = convertedVolumeOf(d.value?.BUY)
    const sellVol = convertedVolumeOf(d.value?.SELL)

    return [
      header,
      row(t('flowSightConversions.convertedVolume'), `R$ ${formatCompactAmount(buyVol + sellVol)}`),
      row(t('flowSightConversions.conversion'), `${(cellRate(d.value) * 100).toFixed(1)}%`),
      `<div style="margin-top:4px">`,
      `<span style="color:${BUY_COLOR}">BUY</span> R$ ${escapeHtml(formatCompactAmount(buyVol))} (${buyObs})`,
      ` • <span style="color:${SELL_COLOR}">SELL</span> R$ ${escapeHtml(formatCompactAmount(sellVol))} (${sellObs})`,
      `</div>`,
    ].join('')
  }

  // A redraw can replace the rect under the cursor, and no mouseout fires for a
  // node that no longer exists.
  chart.on('preRedraw.tooltip', () => tooltip.hide())

  // Decor/UX: hover, cursor e (fallback) gap + labels custom (para temas "teimosos")
  const applyDecor = (c) => {
    const rects = c.selectAll('rect.heat-box, rect.box, g.box-group rect')

    drilldown.applyTransition(rects)
    // (event, datum): these are attached through chart.selectAll(), which is
    // dc's own nested d3-selection 2.0.0. `d3.event` is null here - see
    // createConversionBubbleChart.
    rects
      .on('mouseover.heatHover', function (event, d) {
        d3.select(this).attr('opacity', 0.85)
        if (d) tooltip.show(tooltipHtml(d), event)
      })
      .on('mousemove.heatHover', (event) => tooltip.move(event))
      .on('mouseout.heatHover', function () {
        d3.select(this).attr('opacity', 1)
        tooltip.hide()
      })

    // Fallback para gap (caso .boxPadding não exista)
    let needGapFallback = false
    rects.each(function () {
      if (!d3.select(this).empty()) needGapFallback = true
    })
    if (needGapFallback && typeof chart.boxPadding !== 'function') {
      const GAP = 2
      rects.each(function () {
        const r = d3.select(this)
        const w = (+r.attr('width')) || 0
        const h = (+r.attr('height')) || 0
        const x = (+r.attr('x')) || 0
        const y = (+r.attr('y')) || 0
        const nw = Math.max(0, w - GAP)
        const nh = Math.max(0, h - GAP)
        const nx = x + (w - nw) / 2
        const ny = y + (h - nh) / 2
        r.attr('x', nx).attr('y', ny).attr('width', nw).attr('height', nh)
      })
    }

    // In-cell text, hand-drawn: the rate on top and the converted volume under
    // it in a smaller, dimmer type. A cell is ~171x57px, so both fit with room
    // to spare - the two quantities the cell encodes are then readable without
    // hovering, and the colour ramp only has to carry the comparison.
    c.selectAll('text.cell-label-custom').remove()
    rects.each(function (d) {
      if (!d) return
      const r = d3.select(this)
      const bw = (+r.attr('width')) || 0
      const bh = (+r.attr('height')) || 0
      const x = (+r.attr('x')) || 0
      const y = (+r.attr('y')) || 0
      const cx = x + bw / 2
      const cy = y + bh / 2
      const ink = labelInk(cellColor(d))
      const parent = d3.select(this.parentNode)

      const addText = (dy, size, weight, opacity, content) => {
        parent
          .append('text')
          .attr('class', 'cell-label-custom')
          .attr('text-anchor', 'middle')
          .attr('x', cx)
          .attr('y', cy + dy)
          .text(content)
          .attr('style', `fill:${ink} !important; font-size:${size}px; font-weight:${weight}; fill-opacity:${opacity}; pointer-events:none`)
      }

      if (!cellCounts(d)) {
        addText(4, 13, 600, 1, '–')
        return
      }

      // Both lines share the ink picked from the cell's own fill, so they stay
      // legible at either end of the ramp.
      addText(-2, 13, 600, 1, `${Math.round(cellRate(d.value) * 100)}%`)
      addText(13, 10, 400, 0.8, `R$ ${formatCompactAmount(cellVolume(d.value))}`)
    })
  }

  chart.on('postRender.decor', applyDecor)
  chart.on('postRedraw.decor', applyDecor)
  chart.on('filtered.drilldown', (c) => {
    drilldown.replace('heatmap', c.filters(), { clear: ['bubble'] })
  })

  return chart
}
